import { seitText, tagKompakt, type Pinziel, type Wartezeile } from '@netcon-work/shared'
import { useState } from 'react'
import { useAbpinnen, useMerken, useStumm } from '../../lib/mutationen'
import { Merkfeld } from '../Merkfeld'
import type { Markierungszeile, Schirmzeile } from './lage'
import { AufgabenVerweis, useAufgabenVerweis } from '../AufgabeKontext'

/**
 * **Auf dem Schirm** ([Z-03](../../../../../docs/phasen/zusatz-03-auf-dem-schirm.md),
 * [E-211](../../../../../docs/ENTSCHEIDUNGEN.md#e-211)).
 *
 * ## Zwei Quellen, ein Ort
 *
 * Nils, 24.08.2026: *„Ich würde mir gerne bestimmte Tickets pinnen, bzw. da wo ich eine Frage
 * gestellt habe und auf Antwort warte."* Das sind **zwei Mechanismen**, und dass sie getrennt
 * bleiben, ist der Kern des Pakets:
 *
 * | | 📌 Pin | ⏳ Ich warte |
 * |---|---|---|
 * | entsteht durch | einen Menschen | eine Lage |
 * | endet durch | denselben Menschen | von selbst, sobald jemand antwortet |
 *
 * In einem Feld wäre die Pinnwand nach drei Wochen ein Friedhof – oder der Pin keine Zusage
 * mehr, sondern eine Vermutung. Hier stehen sie nebeneinander, durch eine Linie getrennt.
 *
 * ## Warum die Zone hier steht und nicht in „Ruhig gestellt"
 *
 * „Ruhig gestellt" schiebt weg, was heute nichts von mir braucht, und ist zugeklappt. **Ein
 * Pin, den man aufklappen muss, ist kein Pin.** Deshalb eine eigene Zone, direkt nach „Jetzt" –
 * und wie „Klopft an" verschwindet sie ganz, wenn nichts da ist.
 *
 * ## 📌 und nicht ★
 *
 * Der Stern ist in Work vergeben: *„für dich vorgesehen"* (`task_wunsch`). Der Stern ist die
 * Meinung **anderer** über mich, der Pin meine eigene. Dieselbe Form für beides wäre die erste
 * Verwechslung, die nie wieder aufhört.
 */
export function AufDemSchirm({
  angepinnt,
  warte,
  markierungen,
  schlafend,
}: {
  angepinnt: readonly Schirmzeile[]
  warte: readonly Wartezeile[]
  markierungen: readonly Markierungszeile[]
  schlafend: { anzahl: number; naechste: string | null; zeilen: readonly Schirmzeile[] }
}) {
  const [schlaeferOffen, setSchlaeferOffen] = useState(false)

  /** Leer heißt weg – kein leerer Kasten, keine Null. Dieselbe Regel wie bei „Klopft an". */
  if (
    angepinnt.length === 0 &&
    warte.length === 0 &&
    markierungen.length === 0 &&
    schlafend.anzahl === 0
  )
    return null

  const zaehler = [
    angepinnt.length > 0 ? `${angepinnt.length} angepinnt` : null,
    warte.length > 0 ? `${warte.length} ohne Antwort` : null,
    markierungen.length > 0 ? `${markierungen.length}× markiert` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <section>
      <div className="mb-[9px] flex items-baseline gap-[9px]">
        <h3 className="text-[12px] font-bold tracking-[0.07em] uppercase">Auf dem Schirm</h3>
        {zaehler && <span className="text-xs text-muted">{zaehler}</span>}
      </div>

      {(angepinnt.length > 0 || warte.length > 0 || markierungen.length > 0) && (
        <div className="overflow-hidden rounded-xl border border-line2 bg-surface shadow-task">
          {angepinnt.map((z) => (
            <Pinzeile key={z.schluessel} zeile={z} />
          ))}

          {/* Die Trennlinie ist die Aussage: darüber Entscheidungen, darunter eine Lage. */}
          {angepinnt.length > 0 && warte.length > 0 && <div className="h-px bg-line2" />}

          {warte.map((w) => (
            <Wartezeilen key={w.taskId} zeile={w} />
          ))}

          {/**
           * **Die dritte Herkunft: jemand anders hat mich hineingeschrieben.**
           *
           * Pin = meine eigene Entscheidung · ⏳ = eine Lage, die von selbst endet · 👤 = die
           * Entscheidung **eines anderen** über mich. Drei Herkünfte, deshalb eine dritte
           * Gruppe mit eigener Linie – und nicht unter die Pins gemischt, wo sie aussähe, als
           * hätte man sie sich selbst hingelegt.
           */}
          {markierungen.length > 0 && (angepinnt.length > 0 || warte.length > 0) && (
            <div className="h-px bg-line2" />
          )}

          {markierungen.map((m) => (
            <Markierungszeilen key={m.unteraufgabeId} zeile={m} />
          ))}
        </div>
      )}

      {schlafend.anzahl > 0 && (
        <Schlaefer
          anzahl={schlafend.anzahl}
          naechste={schlafend.naechste}
          zeilen={schlafend.zeilen}
          offen={schlaeferOffen}
          umschalten={() => setSchlaeferOffen((o) => !o)}
        />
      )}
    </section>
  )
}

/* ─────────────────────────── Eine angepinnte Zeile ────────────────────────── */

/** `oeffnen` braucht die Zeile nicht mehr – der Titel ist seit E-319 ein Verweis. */
function Pinzeile({ zeile }: { zeile: Schirmzeile }) {
  const abpinnen = useAbpinnen()
  const [merkenOffen, setMerkenOffen] = useState(false)

  const anklickbar = zeile.ziel.art === 'aufgabe' && !zeile.verwaist

  return (
    <div className="border-t border-line px-3.5 py-2 text-[13px] first:border-t-0">
      {/**
       * **Die Zeile darf umbrechen** ([E-320](../../../../../docs/ENTSCHEIDUNGEN.md#e-320),
       * 04.09.2026).
       *
       * Nils: *„bei dem «Auf dem Schirm» da kann ich bei der ersten bspw. gar nicht klicken."*
       *
       * Der Titel war das **einzige** Element mit `flex-1 min-w-0`, alles andere `shrink-0`:
       * die Marke „heute wieder vorgelegt", zwei Knöpfe, das Zeichen. In einer schmalen Spalte
       * überstieg deren Summe die Breite – und der Titel schrumpfte auf **null Pixel**. Er war
       * da, nur unsichtbar und nicht zu treffen.
       *
       * `flex-wrap` und eine Mindestbreite lösen das an der Wurzel: was nicht mehr passt, geht
       * in die nächste Zeile, statt den einen Teil zu verschlucken, auf den es ankommt.
       */}
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
        <span className="shrink-0 text-[12px]" aria-hidden>
          {ZIELZEICHEN[zeile.ziel.art]}
        </span>

        {anklickbar ? (
          <Schirmtitel
            taskId={(zeile.ziel as { taskId: string }).taskId}
            titel={zeile.titel}
            erledigt={zeile.erledigt}
          />
        ) : (
          <span className="min-w-[8rem] flex-1 truncate text-left" title={zeile.titel}>
            {zeile.titel}
          </span>
        )}

        {/**
         * **Der Weckruf steht am Tag selbst da und danach nicht mehr.** Ohne ihn reiht sich ein
         * aufgewachter Pin lautlos in eine Liste ein, die man längst nur noch überfliegt – und
         * dann war das Datum umsonst ([E-216](../../../../../docs/ENTSCHEIDUNGEN.md#e-216)).
         */}
        {zeile.weckruf && (
          <span className="shrink-0 rounded-md bg-accent-deep px-1.5 py-[1px] text-[10px] font-bold tracking-[0.04em] text-white uppercase">
            heute wieder vorgelegt
          </span>
        )}

        {zeile.erledigt && <span className="shrink-0 text-[11.5px] text-muted">✓ erledigt</span>}

        {zeile.verwaist && (
          <span className="shrink-0 text-[11.5px] text-warn">
            {zeile.ziel.art === 'bereich' ? 'Bereich nicht mehr vorhanden' : 'nicht mehr vorhanden'}
          </span>
        )}

        {!zeile.weckruf && !zeile.erledigt && !zeile.verwaist && zeile.auskunft && (
          <span className="shrink-0 text-[11.5px] text-muted">{zeile.auskunft}</span>
        )}

        <button
          type="button"
          onClick={() => setMerkenOffen((o) => !o)}
          className="shrink-0 cursor-pointer rounded-lg border border-line2 px-2 py-[2px] text-[11px] text-ink2 hover:bg-page"
          title="Notiz und Wiedervorlage"
        >
          {zeile.wiedervorlage ? '🕓' : '✎'}
        </button>
        <button
          type="button"
          onClick={() => abpinnen.mutate(zeile.ziel)}
          className="shrink-0 cursor-pointer rounded-lg border border-line2 px-2 py-[2px] text-[11px] text-ink2 hover:bg-page"
          title="Abpinnen"
        >
          Abpinnen
        </button>
      </div>

      {/* Die Notiz ist der Grund, warum ein Pin eine Woche übersteht. Sie steht deshalb dabei. */}
      {zeile.notiz && !merkenOffen && (
        <p className="mt-[3px] pl-[26px] text-[11.5px] text-ink2 italic">„{zeile.notiz}"</p>
      )}

      {merkenOffen && (
        <Merkfeld
          ziel={zeile.ziel}
          notiz={zeile.notiz}
          wiedervorlage={zeile.wiedervorlage}
          schliessen={() => setMerkenOffen(false)}
          className="mt-2 ml-[26px]"
        />
      )}
    </div>
  )
}

/**
 * **Der Titel als echter Verweis** ([E-319](../../../../../docs/ENTSCHEIDUNGEN.md#e-319)) – und
 * mit einer Mindestbreite, damit er nicht wieder auf null schrumpft (E-320).
 *
 * Eigene Komponente, weil `useAufgabenVerweis` ein Hook ist und die Zeile ihn nur in einem von
 * drei Fällen braucht – ein Hook hinter einer Bedingung wäre der Fehler, den React zu Recht
 * verbietet.
 */
function Schirmtitel({
  taskId,
  titel,
  erledigt,
}: {
  taskId: string
  titel: string
  erledigt: boolean
}) {
  const verweis = useAufgabenVerweis(taskId)
  return (
    <a
      {...verweis}
      className={`min-w-[8rem] flex-1 cursor-pointer truncate text-left hover:text-accent hover:underline ${
        erledigt ? 'text-muted line-through' : ''
      }`}
      title={titel}
    >
      {titel}
    </a>
  )
}

const ZIELZEICHEN: Record<Pinziel['art'], string> = {
  aufgabe: '📌',
  vorhaben: '▸',
  bereich: '⬡',
}

/* ─────────────────────────── Ich warte auf Antwort ────────────────────────── */

function Wartezeilen({ zeile }: { zeile: Wartezeile }) {
  const stumm = useStumm()

  return (
    <div className="flex items-center gap-2.5 border-t border-line px-3.5 py-2 text-[13px] first:border-t-0">
      <span className="shrink-0 text-[12px]" aria-hidden>
        ⏳
      </span>
      <AufgabenVerweis
        aufgabeId={zeile.taskId}
        className="min-w-0 flex-1 cursor-pointer truncate text-left hover:text-accent hover:underline"
        title={zeile.titel}
      >
        {zeile.titel}
      </AufgabenVerweis>

      {/**
       * **Das Alter trägt die Dringlichkeit, nicht die Zone.** Ohne Karenzzeit (Nils:
       * *„sofort"*) steht der eben geschriebene Kommentar sofort hier; die Warnfarbe ab fünf
       * **Werktagen** unterscheidet „läuft" von „da antwortet niemand mehr".
       */}
      <span className={`shrink-0 text-[11.5px] ${zeile.warnt ? 'text-warn' : 'text-muted'}`}>
        {seitText(zeile.tage)}
        {zeile.warnt && ' ⚠'}
      </span>

      <button
        type="button"
        onClick={() => stumm.mutate(zeile.taskId)}
        className="shrink-0 cursor-pointer rounded-lg border border-line2 px-2 py-[2px] text-[11px] text-ink2 hover:bg-page"
        title="War keine Frage – nicht mehr anzeigen"
      >
        erledigt sich
      </button>
    </div>
  )
}

/* ─────────────────────────── Wo ich markiert bin ──────────────────────────── */

/**
 * **Eine Unteraufgabe, in der ich markiert bin** (01.10.2026).
 *
 * Christian Hunhoff, 21.09.2026: *„so weis man im Zweifelsfall nichts von seinem Glück, oder
 * man muss die Nachricht dauerhaft recht aus «ungelesen» stehen lasse."*
 *
 * **Die Unteraufgabe steht vorn, die Aufgabe dahinter** – und nicht umgekehrt. Was mich angeht,
 * ist der Satz, den jemand hineingeschrieben hat („Eingehende Anrufe auch anzeigen?"); die
 * Aufgabe darum herum („Kundenportal 2.0") ist die Adresse. Stünde die Aufgabe vorn, sähe die
 * Zeile aus wie eine Aufgabe, die mir gehört – und genau das ist sie nicht
 * ([E-345](../../../../../docs/ENTSCHEIDUNGEN.md#e-345)).
 *
 * **Kein „erledigt"-Knopf hier.** Das Häkchen sitzt an der Unteraufgabe in der Aufgabe, wo auch
 * steht, worum es geht. Ein zweiter Ort zum Abhaken wäre ein zweiter Zustand, der auseinander
 * läuft; die Zeile verschwindet von selbst, sobald das Häkchen dort gesetzt ist.
 */
function Markierungszeilen({ zeile }: { zeile: Markierungszeile }) {
  return (
    <div className="flex items-center gap-2.5 border-t border-line px-3.5 py-2 text-[13px] first:border-t-0">
      <span className="shrink-0 text-[12px]" aria-hidden title="Du bist hier markiert">
        👤
      </span>
      <AufgabenVerweis
        aufgabeId={zeile.taskId}
        className="min-w-0 flex-1 cursor-pointer truncate text-left hover:text-accent hover:underline"
        title={`${zeile.unteraufgabeTitel} – in „${zeile.aufgabeTitel}"`}
      >
        {zeile.unteraufgabeTitel}
      </AufgabenVerweis>

      <span className="hidden min-w-0 shrink truncate text-[11.5px] text-muted sm:block">
        {zeile.aufgabeTitel}
      </span>

      <span className="shrink-0 text-[11px] text-muted">markiert</span>
    </div>
  )
}

/* ─────────────────────────── Die leise Zeile ──────────────────────────────── */

/**
 * **Der schlafende Pin darf kein schwarzes Loch sein.**
 *
 * Wer etwas für sechs Wochen wegräumt und es nirgends wiederfindet, pinnt es ein zweites Mal
 * oder traut der Funktion nicht mehr. Diese Zeile ist deshalb **immer** da, sobald etwas
 * schläft – auch wenn sonst nichts auf dem Schirm ist. Das ist kein leerer Kasten, sondern eine
 * einzeilige Auskunft.
 */
function Schlaefer({
  anzahl,
  naechste,
  zeilen,
  offen,
  umschalten,
}: {
  anzahl: number
  naechste: string | null
  zeilen: readonly Schirmzeile[]
  offen: boolean
  umschalten: () => void
}) {
  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={umschalten}
        className="cursor-pointer text-left text-[11.5px] text-muted hover:text-ink2"
      >
        🕓 {anzahl === 1 ? 'einer liegt' : `${anzahl} liegen`} später wieder vor
        {naechste && ` · die nächste am ${tagKompakt(naechste)}`}
        <span className="ml-1">{offen ? '▾' : '▸'}</span>
      </button>

      {offen && (
        <div className="mt-1.5 overflow-hidden rounded-xl border border-line bg-surface">
          {zeilen.map((z) => (
            <div
              key={z.schluessel}
              className="border-t border-line px-3.5 py-1.5 text-[12.5px] first:border-t-0"
            >
              <div className="flex items-center gap-2.5">
                <span className="shrink-0 text-[11px] text-muted">
                  {z.wiedervorlage ? tagKompakt(z.wiedervorlage) : ''}
                </span>
                {z.ziel.art === 'aufgabe' && !z.verwaist ? (
                  <AufgabenVerweis
                    aufgabeId={(z.ziel as { taskId: string }).taskId}
                    className="min-w-0 flex-1 cursor-pointer truncate text-left hover:text-accent hover:underline"
                  >
                    {z.titel}
                  </AufgabenVerweis>
                ) : (
                  <span className="min-w-0 flex-1 truncate">{z.titel}</span>
                )}
                <HolenJetzt ziel={z.ziel} notiz={z.notiz} />
              </div>
              {z.notiz && <p className="pl-[42px] text-[11px] text-ink2 italic">„{z.notiz}"</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Ein schlafender Pin lässt sich vorzeitig wecken – die Wiedervorlage fällt weg, der Pin bleibt.
 *
 * **Die Notiz wird mitgereicht und nicht überschrieben.** `merken` schreibt beide Felder in
 * einem Zug; sie hier wegzulassen hieße, sie zu löschen – und damit ginge genau die Auskunft
 * verloren, für die es die Notiz gibt.
 */
function HolenJetzt({ ziel, notiz }: { ziel: Pinziel; notiz: string | null }) {
  const merken = useMerken()

  return (
    <button
      type="button"
      onClick={() => merken.mutate({ ziel, notiz, wiedervorlage: null })}
      className="shrink-0 cursor-pointer text-[11px] text-muted underline hover:text-ink2"
      title="Jetzt schon auf den Schirm holen"
    >
      jetzt holen
    </button>
  )
}
